import {
    Add, Delete, DragIndicator,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    TableCell,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import {
    CollisionDetection,
    closestCenter,
    pointerWithin,
} from '@dnd-kit/core';
import {
    KeyboardEvent, useCallback, useMemo, useState,
} from 'react';
import { SortableRankingTable, SortableRankingTableColumn } from 'src/components/common/DesignConceptRankingTable/SortableRankingTable';
import { DEFAULT_CULTURE } from 'src/constants';
import { DesignConceptPreview } from 'src/components/ForcedRankingDetails/DesignConceptPreview';
import { WidgetComponentProps, WidgetOption } from 'src/components/SetsPlayground/Widgets/WidgetSelector';

type InlineSetRanking = Models.SetsPlayground.InlineSetRanking;

type Props = WidgetComponentProps<InlineSetRanking>

interface Row {
    id: string;
    designConceptId: string;
}

const AddRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '120px 1fr 160px auto',
    gap: theme.spacing(2),
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
    },
}));

const toRows = (value: InlineSetRanking): Row[] => value.designConceptIds.map((designConceptId) => ({
    id: designConceptId,
    designConceptId,
}));

const toRanking = (value: InlineSetRanking, rows: Row[]): InlineSetRanking => ({
    ...value,
    designConceptIds: rows.map((row) => row.designConceptId),
});

const normalizePosition = (rawValue: string): number | null => {
    const parsed = parseInt(rawValue, 10);

    return Number.isNaN(parsed) || parsed < 1 ? null : parsed;
};

const move = <T, >(items: T[], fromIndex: number, toIndex: number): T[] => {
    const next = [...items];
    const [item] = next.splice(fromIndex, 1);

    next.splice(toIndex, 0, item);
    return next;
};

const insertAtRank = (rows: Row[], sourceIndex: number, rank: number): Row[] => move(
    rows,
    sourceIndex,
    Math.min(rank - 1, rows.length - 1),
);

const pointerFirstCollisionDetection: CollisionDetection = (args) => {
    const pointerHits = pointerWithin(args).filter((collision) => collision.id !== args.active.id);

    if (pointerHits.length > 0) {
        return pointerHits;
    }

    return closestCenter(args).filter((collision) => collision.id !== args.active.id);
};

const DesignConceptInlineRankingWidgetComponent = ({
    value,
    onChange,
    productKey,
    cultures,
}: Props): JSX.Element => {
    const [newDesignConceptId, setNewDesignConceptId] = useState('');
    const [newRankInput, setNewRankInput] = useState('');
    const [editingRankIndex, setEditingRankIndex] = useState<number | null>(null);
    const [editingRankValue, setEditingRankValue] = useState('');
    const [newConceptPreviewStatus, setNewConceptPreviewStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const rows = useMemo(() => toRows(value), [value]);
    const safeProductKey = productKey ?? '';
    const safeCultures = cultures ?? [DEFAULT_CULTURE];

    const trimmedNewDesignConceptId = newDesignConceptId.trim();
    const isDuplicateNewDesignConceptId = useMemo(
        () => trimmedNewDesignConceptId !== ''
            && rows.some((row) => row.designConceptId === trimmedNewDesignConceptId),
        [rows, trimmedNewDesignConceptId],
    );
    const isInvalidNewDesignConceptId = trimmedNewDesignConceptId !== ''
        && newConceptPreviewStatus === 'error';
    let newDesignConceptIdHelperText: string | undefined;

    if (isDuplicateNewDesignConceptId) {
        newDesignConceptIdHelperText = 'Design concept already added';
    } else if (isInvalidNewDesignConceptId) {
        newDesignConceptIdHelperText = 'Design concept id may not be valid for this product';
    }

    const handlePreviewStatusChange = useCallback((status: 'idle' | 'loading' | 'success' | 'error'): void => {
        setNewConceptPreviewStatus(status);
    }, []);

    const commitRows = (nextRows: Row[]): void => {
        onChange(toRanking(value, nextRows));
    };

    const handleAdd = (): void => {
        const designConceptId = newDesignConceptId?.trim();

        if (!designConceptId || isDuplicateNewDesignConceptId || isInvalidNewDesignConceptId) {
            return;
        }

        const rank = normalizePosition(newRankInput);
        let nextRows = [...rows, { id: designConceptId, designConceptId }];

        if (rank) {
            nextRows = insertAtRank(nextRows, nextRows.length - 1, rank);
        }

        commitRows(nextRows);
        setNewDesignConceptId('');
        setNewRankInput('');
        setNewConceptPreviewStatus('idle');
    };

    const handleRemove = (index: number): void => {
        if (editingRankIndex === index) {
            setEditingRankIndex(null);
            setEditingRankValue('');
        }

        commitRows(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    const handleRankChange = (index: number, rawPosition: string): void => {
        const rank = normalizePosition(rawPosition);

        if (rank === null) {
            return;
        }

        commitRows(insertAtRank(rows, index, rank));
    };

    const beginRankEditing = (index: number): void => {
        setEditingRankIndex(index);
        setEditingRankValue(String(index + 1));
    };

    const finishRankEditing = (): void => {
        if (editingRankIndex === null) {
            return;
        }

        handleRankChange(editingRankIndex, editingRankValue);
        setEditingRankIndex(null);
        setEditingRankValue('');
    };

    const handleRankKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            finishRankEditing();
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            setEditingRankIndex(null);
            setEditingRankValue('');
        }
    };

    const handleReorder = (oldIndex: number, newIndex: number): void => {
        commitRows(move(rows, oldIndex, newIndex));
    };

    const columns: SortableRankingTableColumn[] = [
        { key: 'rank', label: 'Rank', width: 88 },
        { key: 'preview', label: 'Preview' },
        { key: 'conceptId', label: 'Design concept ID' },
        { key: 'action', label: 'Action', align: 'right' },
    ];

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info">
                Inline ranking stores design concepts not design ids. Rank sets list order only.
            </Alert>
            <AddRow>
                {safeProductKey && newDesignConceptId ? (
                    <DesignConceptPreview
                        conceptId={newDesignConceptId}
                        cultures={safeCultures}
                        productKey={safeProductKey}
                        onStatusChange={handlePreviewStatusChange}
                    />
                ) : (
                    <Box sx={{ width: 100, height: 100 }} />
                )}
                <Box alignItems="center" display="flex" gap={1}>
                    <TextField
                        fullWidth
                        error={isDuplicateNewDesignConceptId || isInvalidNewDesignConceptId}
                        helperText={newDesignConceptIdHelperText ?? ' '}
                        label="Design concept ID"
                        placeholder="Search by concept ID"
                        size="small"
                        value={newDesignConceptId}
                        onChange={(e): void => {
                            setNewDesignConceptId(e.target.value);
                            setNewConceptPreviewStatus('idle');
                        }}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAdd();
                            }
                        }}
                    />
                </Box>
                <TextField
                    helperText=" "
                    label="Rank"
                    placeholder="Auto"
                    size="small"
                    type="number"
                    value={newRankInput}
                    onChange={(e): void => setNewRankInput(e.target.value)}
                    onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
                    <Button
                        disabled={
                            !trimmedNewDesignConceptId
                            || isDuplicateNewDesignConceptId
                            || isInvalidNewDesignConceptId
                            || newConceptPreviewStatus === 'loading'
                        }
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                    <Box sx={{ minHeight: 20 }} />
                </Box>
            </AddRow>
            {rows.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                    No design concepts added yet.
                </Typography>
            ) : (
                <SortableRankingTable
                    autoScroll={false}
                    collisionDetection={pointerFirstCollisionDetection}
                    columns={columns}
                    dragHandleHeader={<DragIndicator color="disabled" sx={{ ml: 2 }} />}
                    getItemId={(row): string => row.id}
                    items={rows}
                    renderRow={(row, index): JSX.Element => (
                        <>
                            <TableCell sx={{ width: 88 }}>
                                {editingRankIndex === index ? (
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                        size="small"
                                        type="number"
                                        value={editingRankValue}
                                        onBlur={finishRankEditing}
                                        onChange={(e): void => setEditingRankValue(e.target.value)}
                                        onKeyDown={handleRankKeyDown}
                                    />
                                ) : (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        sx={{ minWidth: 0, px: 1 }}
                                        onClick={(): void => beginRankEditing(index)}
                                    >
                                        {index + 1}
                                    </Button>
                                )}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                                <DesignConceptPreview
                                    conceptId={row.designConceptId}
                                    cultures={safeCultures}
                                    productKey={safeProductKey}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">{row.designConceptId}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Button
                                    color="error"
                                    size="small"
                                    startIcon={<Delete />}
                                    onClick={(): void => handleRemove(index)}
                                >
                                    Remove
                                </Button>
                            </TableCell>
                        </>
                    )}
                    tableSize="small"
                    onReorder={handleReorder}
                />
            )}
        </Box>
    );
};

export const DesignConceptInlineRankingWidget: WidgetOption<InlineSetRanking> = {
    label: 'Inline Ranking',
    value: 'inline',
    helperText: 'Search and order design concepts by rank.',
    component: DesignConceptInlineRankingWidgetComponent,
    getDefaultValue: () => ({ type: 'inline', designConceptIds: [], elementFilters: [] }),
};
